import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { WhatsNewEntry } from "../../core/models/entities/WhatsNewEntry";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { mapToWhatsNewEntry } from "../models/mappers/mapWhatsNewEntry";
import { WhatsNewEntryDto } from "../models/responses/WhatsNewDto";

export interface ContentRepository {
  // Fail-silent: any API error resolves to an empty list (the header button
  // hides itself) - the changelog must never break the app shell. Entries
  // keep the API's newest-first order.
  useWhatsNew(): Resource<WhatsNewEntry[]>;
}

export function useContentRepository(): ContentRepository {
  const apiClient = useApiClient();

  function useWhatsNew(): Resource<WhatsNewEntry[]> {
    // Subscribes this component to language changes, so switching language
    // re-runs `select` below and the panel flips en<->da without a refetch
    // (the cache holds the raw DTOs, which carry both languages).
    const { i18n } = useTranslation();
    const language = i18n.language ?? "en";

    return useQuery<WhatsNewEntryDto[], unknown, WhatsNewEntry[]>(
      ["whats-new"],
      () =>
        apiClient
          .getWhatsNew()
          .then((response) => response.entries)
          // Errors resolve as success-with-empty so React Query does not
          // retry-hammer a broken endpoint on every header render.
          .catch(() => [] as WhatsNewEntryDto[]),
      {
        select: (entries) =>
          entries.map((dto) => mapToWhatsNewEntry(dto, language)),
        // No polling beyond React Query's staleness: at most one fetch per
        // 30 minutes, and only when a component actually mounts the hook.
        staleTime: 30 * 60 * 1000,
      },
    );
  }

  return { useWhatsNew };
}
